import SelectComponent from "@via/components/select/select.tsx";
import { useFileOrganizer } from "@via/hooks/useFileOrganizer.tsx";
import { useSaveFilesToCollection } from "@via/pages/data/data-collections/data-collections.api.tsx";
import FileOrganizerForm, {
  type IFileOrganizerForm,
} from "@via/pages/data/file-organizer/file-organizer-form.tsx";
import FilePatterns from "@via/pages/data/file-organizer/file-patterns/file-patterns.tsx";
import SampleTable from "@via/pages/data/file-organizer/sample-table.tsx";
import { CollectionTypes } from "@via/utils/constants.tsx";
import { classNames, removeFalsyValues } from "@via/utils/helpers.tsx";
import { type SelectOption } from "@via/utils/interfaces.tsx";
import {
  DataSourceProvider,
  FileArrayItem,
  FileDirItem,
  SaveFileRequestBody,
  type SelectedFile,
} from "common";
import { type JSX, useEffect, useState } from "react";
import axios from "axios";
import FileExtensionModal from "@via/pages/data/file-organizer/file-extension-modal.tsx";
import PremiumSampleOrganizer from "@via/pages/data/file-organizer/premium-sample-organizer.tsx";
import { buildSaveFileDataSource } from "@via/pages/data/file-organizer/helpers.ts";
import { useGlobal } from "@via/hooks/useGlobal.tsx";
import { useSearchParams } from "react-router-dom";

export interface IImportedFile extends SelectedFile {
  isChecked?: boolean;
  isSampled?: boolean;
}

export default function FileOrganizer({
  importedFiles,
  globalBucketName,
  onBackToSelectionClick,
  credentialId = null,
  serviceTokenId,
  dataSourceId = null,
  dataSourceName,
  provider,
  runEnvId,
}: {
  importedFiles: IImportedFile[];
  globalBucketName: string;
  onBackToSelectionClick: () => void;
  credentialId: number | null;
  dataSourceId?: number | null;
  dataSourceName?: string;
  serviceTokenId?: number;
  provider: DataSourceProvider;
  runEnvId?: number;
}): JSX.Element {
  const [searchParams] = useSearchParams();
  const {
    isSelectedCollectionPremium: contextIsPremium,
    setIsSelectedCollectionPremium,
  } = useGlobal();

  // Read datasetId from URL params - if it exists, it's a premium collection
  const datasetId = searchParams.get("datasetId");
  const isSelectedCollectionPremium = datasetId ? true : contextIsPremium;

  // Update context if datasetId exists in URL
  useEffect(() => {
    if (datasetId && !contextIsPremium) {
      setIsSelectedCollectionPremium(true);
    }
  }, [datasetId, contextIsPremium, setIsSelectedCollectionPremium]);

  const [isExtensionModalOpen, setIsExtensionModalOpen] = useState(false);
  const [suggestedExtension, setSuggestedExtension] = useState("");
  const [
    saveFilesToCollectionPayloadWithoutExtension,
    setSaveFilesToCollectionPayloadWithoutExtension,
  ] = useState<SaveFileRequestBody | undefined>(undefined);
  const { selectedSamples } = useFileOrganizer();

  const [selectedCollectionType, setSelectedCollectionType] =
    useState<SelectOption>(CollectionTypes[0]);

  const {
    mutateAsync: saveFilesToCollection,
    isPending: isSaveFilesToCollectionLoading,
  } = useSaveFilesToCollection();

  const handleFormSave = async (
    formPayload: IFileOrganizerForm
  ): Promise<void> => {
    const fileArray: FileArrayItem[] = [];
    const fileDir: FileDirItem[] = [];

    selectedSamples?.forEach(sample => {
      const formattedSample: FileArrayItem = {
        name: sample.name,
        filesUsed: sample.filesUsed,
      };
      /**
       * Save file id's if the provider is BaseSpace
       */
      if (provider === DataSourceProvider.BaseSpace) {
        formattedSample.baseSpaceId = sample?.id;
      }
      fileArray.push(formattedSample);

      /**
       * Save file directory with the connection credential id for pipelines
       * Only add credential id for Amazon S3 and Google Cloud Data Sources
       */
      let fileDirItem: FileDirItem = { directory: sample.directory };
      if (credentialId) fileDirItem.credentialId = credentialId;
      if (provider === DataSourceProvider.BaseSpace)
        fileDirItem = { directory: "" };
      fileDir.push(fileDirItem);
    });

    const dataSource = buildSaveFileDataSource({
      provider,
      dataSourceId,
      serviceTokenId,
      runEnvId,
    });

    const saveFilesToCollectionPayload = {
      dataSource,
      collectionType: selectedCollectionType.value,
      ...formPayload,
      fileArray,
      fileDir,
    };

    /*
     * Add amazon s3 archive credential id to archive path
     */
    if (saveFilesToCollectionPayload.s3ArchiveCredentialId) {
      saveFilesToCollectionPayload.s3ArchiveDir = `${saveFilesToCollectionPayload.s3ArchiveDir}\t${saveFilesToCollectionPayload.s3ArchiveCredentialId}`;
      delete saveFilesToCollectionPayload.s3ArchiveCredentialId;
    }

    /*
     * Add google cloud archive credential id to archive path
     */
    if (saveFilesToCollectionPayload.gsArchiveCredentialId) {
      saveFilesToCollectionPayload.gsArchiveDir = `${saveFilesToCollectionPayload.gsArchiveDir}\t${saveFilesToCollectionPayload.gsArchiveCredentialId}`;
      delete saveFilesToCollectionPayload.gsArchiveCredentialId;
    }

    try {
      await saveFilesToCollection(
        removeFalsyValues(saveFilesToCollectionPayload)
      );
    } catch (error: unknown) {
      if (axios.isAxiosError(error)) {
        if (error.status === 455) {
          setSaveFilesToCollectionPayloadWithoutExtension(
            saveFilesToCollectionPayload as unknown as SaveFileRequestBody
          );
          setIsExtensionModalOpen(true);
          setSuggestedExtension(
            error?.response?.data?.suggestedExtension || ""
          );
        }
      }
    }
  };

  const handleFileExtensionUpdate = async (fileExtension: string) => {
    await saveFilesToCollection(
      removeFalsyValues({
        userProvidedFileType: fileExtension,
        ...saveFilesToCollectionPayloadWithoutExtension,
      })
    );
  };

  const handleBackToSelectionButton = () => {
    onBackToSelectionClick();
  };

  const [toBeRemovedFileIds, setToBeRemovedFileIds] = useState<string[]>([]);
  const handleSampleRemove = (sampleId: string) => {
    setToBeRemovedFileIds(sampleId.split(","));
  };

  return (
    <div
      className={classNames(
        "p-4",
        isSaveFilesToCollectionLoading ? "pointer-events-none" : ""
      )}>
      <div className="flex justify-end">
        <button
          data-testid="backToSelectionButton"
          onClick={handleBackToSelectionButton}
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          Back to Selection
        </button>
      </div>
      <div className="mt-4 flex gap-4">
        <div className="flex flex-1 flex-col gap-2">
          <label>Dataset Type</label>
          <div data-testid="collectionTypeSelector">
            <SelectComponent
              name="collectionType"
              options={CollectionTypes}
              defaultValue={CollectionTypes[0]}
              placeholder="Select a dataset type"
              onChange={e => {
                setSelectedCollectionType(e as SelectOption);
              }}
              enableClear={false}
            />
            {!selectedCollectionType && (
              <span className="text-xs text-rose-500">
                Dataset type is required
              </span>
            )}
          </div>
        </div>
      </div>

      <FilePatterns
        fileList={importedFiles}
        collectionType={selectedCollectionType}
        globalBucketName={globalBucketName}
        toBeRemovedFileIds={toBeRemovedFileIds}
        provider={provider}
      />

      {!isSelectedCollectionPremium &&
        (selectedSamples?.length ? (
          <SampleTable onSampleRemove={handleSampleRemove} />
        ) : (
          <div className="my-10 flex h-40 items-center justify-center rounded-md border border-gray-200 py-4">
            No Files Are Added
          </div>
        ))}

      {isSelectedCollectionPremium ? (
        <PremiumSampleOrganizer
          collectionType={(selectedCollectionType.value || "").toString()}
          provider={provider}
          credentialId={credentialId}
          serviceTokenId={serviceTokenId}
          dataSourceName={dataSourceName}
        />
      ) : (
        <FileOrganizerForm
          onFormSave={handleFormSave}
          isLoading={isSaveFilesToCollectionLoading}
        />
      )}

      <FileExtensionModal
        open={isExtensionModalOpen}
        setOpen={setIsExtensionModalOpen}
        suggestedExtension={suggestedExtension}
        onConfirm={handleFileExtensionUpdate}
      />
    </div>
  );
}
